/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
} from "react";
import {
  useCmsSpecVersions,
  resolveStateDefaultCmsSpecVersion,
  useStateDefaultCmsSpecVersion,
} from "@/api/hooks/useCmsSpecVersions";

export type EnvironmentFilter = "All" | "Production" | "Test";

interface FilterContextType {
  environmentFilter: EnvironmentFilter;
  setEnvironmentFilter: (filter: EnvironmentFilter) => void;
  // CMS spec version filter: "All" or a cms_spec_version code (e.g. "V4.1.0").
  // Mirrors environmentFilter; "All" => no filter (backend reads the all-versions rollup).
  cmsSpecVersionFilter: string;
  setCmsSpecVersionFilter: (filter: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  // Helper to get API-compatible environment value
  getEnvironmentParam: () => string | undefined;
  // Helper to get API-compatible cms_spec_version value ("All" => undefined)
  getCmsSpecVersionParam: () => string | undefined;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [environmentFilter, setEnvironmentFilter] = useState<EnvironmentFilter>("All");
  const [cmsSpecVersionFilter, setCmsSpecVersionFilter] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Seed the version filter from State Configuration once, so version-scoped
  // pages (Analytics) open on the state's configured CMS spec version instead
  // of the all-versions rollup. This provider is mounted once at the app root
  // and never unmounts, so `didSeedVersionRef` guards against re-seeding and a
  // later manual change by the user is preserved.
  //
  // We seed only once the state config has genuinely settled (`stateReady`) AND a real
  // `stateVersion` is known — never on a stale cached value during a background refetch, and never
  // on a fallback while it's still resolving. Either would permanently latch `didSeedVersionRef`
  // and prevent correction once the settled value arrives. If no state config ever loads, the
  // filter stays at its "All" default (the original behavior).
  const { data: cmsSpecVersions = [] } = useCmsSpecVersions();
  const { version: stateVersion, isReady: stateReady } = useStateDefaultCmsSpecVersion();
  const didSeedVersionRef = useRef(false);
  useEffect(() => {
    if (didSeedVersionRef.current || !stateReady || !stateVersion || cmsSpecVersions.length === 0)
      return;
    const resolved = resolveStateDefaultCmsSpecVersion(cmsSpecVersions, stateVersion);
    if (!resolved) return;
    didSeedVersionRef.current = true;
    setCmsSpecVersionFilter(resolved.cms_spec_version);
  }, [cmsSpecVersions, stateVersion, stateReady]);

  // Convert filter to API parameter value
  const getEnvironmentParam = useCallback(() => {
    if (environmentFilter === "Production") return "P";
    if (environmentFilter === "Test") return "T";
    return undefined; // "All" means no filter
  }, [environmentFilter]);

  const getCmsSpecVersionParam = useCallback(() => {
    return cmsSpecVersionFilter && cmsSpecVersionFilter !== "All"
      ? cmsSpecVersionFilter
      : undefined; // "All" means no filter
  }, [cmsSpecVersionFilter]);

  return (
    <FilterContext.Provider
      value={{
        environmentFilter,
        setEnvironmentFilter,
        cmsSpecVersionFilter,
        setCmsSpecVersionFilter,
        searchQuery,
        setSearchQuery,
        getEnvironmentParam,
        getCmsSpecVersionParam,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFilter() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
}
